import type {
  WavesChart,
  WavesTerrain,
  WavesTerrainNode,
  WavesTerrainWall,
} from '@shared/wavesChart';
import { resolveWavesTerrain, surfaceYAt, terrainWalls } from '@shared/wavesChart';
import type { WavesLayout } from '../wavesPlayfieldGeometry';
import {
  canvasXToBeat,
  obstacleCanvasX,
  playfieldYToCanvas,
} from '../wavesPlayfieldGeometry';
import { BEAT_PULSE_STRENGTH, DEATH_COLOUR } from './wavesRenderConstants';

interface CanvasPoint {
  x: number;
  y: number;
}

interface CorridorTerrain {
  terrain: WavesTerrain;
  walls: WavesTerrainWall[];
}

/**
 * Resolved terrain + derived wall list, cached per chart. `drawWavesCorridor`
 * runs every frame (60 fps), but a chart's terrain never changes — without
 * this, each frame re-resolved the terrain and re-sorted the wall list. The
 * `WeakMap` key lets caches drop when a chart is GC'd and stays correct if
 * two playfields render different charts (unlike a single-entry field).
 */
const corridorTerrainCache = new WeakMap<WavesChart, CorridorTerrain>();

function getCorridorTerrain(chart: WavesChart): CorridorTerrain {
  let cached = corridorTerrainCache.get(chart);
  if (!cached) {
    const terrain = resolveWavesTerrain(chart);
    cached = { terrain, walls: terrainWalls(terrain) };
    corridorTerrainCache.set(chart, cached);
  }
  return cached;
}

/**
 * Sample a terrain surface polyline across the visible beat window into
 * canvas points. Endpoints are pinned to the left/right canvas edges
 * (via {@link surfaceYAt}) so the corridor fills the full viewport with
 * no gaps in the letterbox margins; interior terrain nodes are emitted
 * verbatim, so a wall's duplicate-beat pair becomes a vertical segment
 * in the returned path.
 */
function surfacePoints(
  nodes: ReadonlyArray<WavesTerrainNode>,
  layout: WavesLayout,
  currentBeat: number,
  beatLeft: number,
  beatRight: number,
  leftEdgeX: number,
  rightEdgeX: number,
): CanvasPoint[] {
  const pts: CanvasPoint[] = [
    { x: leftEdgeX, y: playfieldYToCanvas(layout, surfaceYAt(nodes, beatLeft)) },
  ];
  for (const node of nodes) {
    if (node.beat <= beatLeft) {
      continue;
    }
    if (node.beat >= beatRight) {
      break;
    }
    pts.push({
      x: obstacleCanvasX(layout, currentBeat, node.beat),
      y: playfieldYToCanvas(layout, node.y),
    });
  }
  pts.push({ x: rightEdgeX, y: playfieldYToCanvas(layout, surfaceYAt(nodes, beatRight)) });
  return pts;
}

/**
 * Fill the solid "rock" region bounded by a surface polyline on one
 * side and a flat playfield edge (`edgeY`) on the other. The surface
 * points run left→right; the polygon walks the edge left→right then the
 * surface right→left and closes.
 */
function fillRockRegion(
  ctx: CanvasRenderingContext2D,
  surface: ReadonlyArray<CanvasPoint>,
  leftEdgeX: number,
  rightEdgeX: number,
  edgeY: number,
): void {
  ctx.beginPath();
  ctx.moveTo(leftEdgeX, edgeY);
  ctx.lineTo(rightEdgeX, edgeY);
  for (let i = surface.length - 1; i >= 0; i -= 1) {
    ctx.lineTo(surface[i].x, surface[i].y);
  }
  ctx.closePath();
  ctx.fill();
}

function strokeSurface(
  ctx: CanvasRenderingContext2D,
  surface: ReadonlyArray<CanvasPoint>,
): void {
  ctx.beginPath();
  ctx.moveTo(surface[0].x, surface[0].y);
  for (let i = 1; i < surface.length; i += 1) {
    ctx.lineTo(surface[i].x, surface[i].y);
  }
  ctx.stroke();
}

/**
 * Paint the winding corridor: solid terrain fills above the ceiling
 * polyline and below the floor polyline, a beat-pulsing edge line along
 * each surface, and a bright red face on every lethal vertical wall so
 * the "slopes glide, walls kill" rule reads at a glance.
 *
 * Terrain spans the FULL canvas, not just the letterboxed playfield —
 * the rock fills and surface lines run edge to edge across every
 * letterbox margin (sides on wide viewports, top/bottom on tall ones)
 * so the corridor never leaves a dark gap at the viewport edges. Only
 * gameplay positioning (player anchor, obstacle approach) stays bound
 * to the centred playfield.
 */
export function drawWavesCorridor(
  ctx: CanvasRenderingContext2D,
  layout: WavesLayout,
  chart: WavesChart,
  beat: number,
  canvasWidth: number,
  canvasHeight: number,
  beatPulse: number,
): void {
  const { terrain, walls } = getCorridorTerrain(chart);
  const palette = chart.palette;
  const leftEdgeX = 0;
  const rightEdgeX = canvasWidth;
  const topEdgeY = 0;
  const bottomEdgeY = canvasHeight;
  const beatLeft = canvasXToBeat(layout, beat, leftEdgeX);
  const beatRight = canvasXToBeat(layout, beat, rightEdgeX);

  const ceiling = surfacePoints(
    terrain.ceiling,
    layout,
    beat,
    beatLeft,
    beatRight,
    leftEdgeX,
    rightEdgeX,
  );
  const floor = surfacePoints(
    terrain.floor,
    layout,
    beat,
    beatLeft,
    beatRight,
    leftEdgeX,
    rightEdgeX,
  );

  ctx.save();

  ctx.fillStyle = palette.bgAccent;
  fillRockRegion(ctx, ceiling, leftEdgeX, rightEdgeX, topEdgeY);
  fillRockRegion(ctx, floor, leftEdgeX, rightEdgeX, bottomEdgeY);

  ctx.strokeStyle = palette.ui;
  ctx.globalAlpha = 0.35 + beatPulse * BEAT_PULSE_STRENGTH * 0.35;
  ctx.lineWidth = 2 + beatPulse * 2;
  ctx.lineJoin = 'round';
  strokeSurface(ctx, ceiling);
  strokeSurface(ctx, floor);

  drawLethalWalls(ctx, layout, walls, beat, beatLeft, beatRight, beatPulse);

  ctx.restore();
}

/**
 * Overlay a thick red edge on each lethal vertical wall in view. A
 * floor wall (upward step) and a ceiling wall (downward step) both read
 * as a sheer red face — the colour vocabulary shared with the death FX.
 */
function drawLethalWalls(
  ctx: CanvasRenderingContext2D,
  layout: WavesLayout,
  walls: ReadonlyArray<WavesTerrainWall>,
  beat: number,
  beatLeft: number,
  beatRight: number,
  beatPulse: number,
): void {
  if (walls.length === 0) {
    return;
  }
  ctx.globalAlpha = 1;
  ctx.strokeStyle = DEATH_COLOUR;
  ctx.lineCap = 'round';
  ctx.lineWidth = 4 + beatPulse * 2;
  for (const wall of walls) {
    if (wall.beat <= beatLeft || wall.beat >= beatRight) {
      continue;
    }
    const x = obstacleCanvasX(layout, beat, wall.beat);
    const topCanvas = playfieldYToCanvas(layout, wall.top);
    const bottomCanvas = playfieldYToCanvas(layout, wall.bottom);
    ctx.beginPath();
    ctx.moveTo(x, topCanvas);
    ctx.lineTo(x, bottomCanvas);
    ctx.stroke();
  }
}
